// Menu model mirroring the real ABP menu contributors:
//  - ClientAdminMenuContributor (role "admin") → Settings + Administration groups
//  - UserMenuContributor (non-admin tenant users) → the feature tree
// `to` is the React route for migrated pages, else the legacy URL (routed to the
// "not migrated yet" placeholder). Permission strings match grantedPolicies.

export interface MenuItem {
  key: string;
  label: string;
  icon?: string;
  to?: string;
  permission?: string;
  live?: boolean;
  migrated?: boolean;
  children?: MenuItem[];
}

export interface MenuSection {
  label?: string;
  items: MenuItem[];
}

// Tenant admin (role: admin) — matches ClientAdminMenuContributor.
const ADMIN_SECTIONS: MenuSection[] = [
  {
    label: 'Administration',
    items: [
      { key: 'users', label: 'Users', icon: 'fa fa-users', to: '/admin/identity/users', permission: 'AbpIdentity.Users', migrated: true },
      { key: 'roles', label: 'Roles', icon: 'fa fa-user-shield', to: '/admin/identity/roles', permission: 'AbpIdentity.Roles', migrated: true },
    ],
  },
  {
    // Legacy's tenant-admin menu has only these three under Settings — no
    // setting-management item (that page stays host-only + reachable by URL).
    label: 'Settings',
    items: [
      { key: 'tenant-config', label: 'Configuration', icon: 'fa fa-cog', to: '/admin/tenant-configuration', permission: 'User.TenantSettings', migrated: true },
      { key: 'email-recipients', label: 'Email Recipients', icon: 'fa fa-envelope', to: '/admin/email-recipients', permission: 'User.TenantSettings.EmailRecipients', migrated: true },
      { key: 'providers', label: 'Providers', icon: 'fa fa-building-columns', to: '/admin/providers', permission: 'User.Providers.View', migrated: true },
    ],
  },
];

// Non-admin tenant users — matches UserMenuContributor.
const USER_SECTIONS: MenuSection[] = [
  {
    items: [
      {
        key: 'risk', label: 'Risk Monitoring', icon: 'fe fe-airplay', permission: 'User.RiskMonitoring',
        children: [
          { key: 'risk-dashboard', label: 'Dashboard', icon: 'fe fe-bar-chart-2', to: '/User/RiskMonitoring/RiskDashboard', permission: 'User.RiskMonitoring.RiskDashboard', live: true },
          { key: 'nop', label: 'Net Open Position', icon: 'fe fe-layers', to: '/User/RiskMonitoring/ConsolidatedExposure', permission: 'User.RiskMonitoring.ConsolidatedExposure', live: true },
          { key: 'nop-stress', label: 'NOP Stress Test', icon: 'fe fe-activity', to: '/User/RiskMonitoring/NopStressTest', permission: 'User.RiskMonitoring.NopStressTest' },
          { key: 'lp-accounts', label: 'LP Accounts', icon: 'fe fe-airplay', to: '/User/RiskMonitoring/LPAccounts', permission: 'User.RiskMonitoring.LPAccount', live: true },
          { key: 'automap', label: 'Auto Map', icon: 'mdi mdi-animation', to: '/User/RiskMonitoring/AutoMap', permission: 'User.RiskMonitoring.AutoMap' },
          { key: 'analytics', label: 'Analytics', icon: 'fe fe-pie-chart', to: '/User/RiskMonitoring/Analytics', permission: 'User.RiskMonitoring.Analytics' },
          { key: 'sigma', label: 'Sigma', icon: 'fe fe-trending-up', to: '/User/RiskMonitoring/Sigma', permission: 'User.RiskMonitoring.Sigma' },
          { key: 'client-prof', label: 'Client Profitability', icon: 'fe fe-bar-chart-2', to: '/User/RiskMonitoring/ClientProfitability', permission: 'User.RiskMonitoring.ClientProfitability' },
          { key: 'portfolio-equity', label: 'Portfolio Equity', icon: 'fe fe-briefcase', to: '/User/RiskMonitoring/PortfolioEquity', permission: 'User.RiskMonitoring.PortfolioEquity' },
          { key: 'swaps-scanner', label: 'Swaps Scanner', icon: 'fe fe-trending-up', to: '/User/RiskMonitoring/SwapsScanner', permission: 'User.RiskMonitoring.SwapsScanner' },
          { key: 'feed-monitoring', label: 'Feed Monitoring', icon: 'fe fe-activity', to: '/User/RiskMonitoring/FeedMonitoring', permission: 'User.RiskMonitoring.FeedMonitoring', live: true, migrated: true },
          { key: 'spread-monitoring', label: 'Spread Monitoring', icon: 'fe fe-bar-chart-2', to: '/User/RiskMonitoring/SpreadMonitoring', permission: 'User.RiskMonitoring.SpreadMonitoring' },
          { key: 'instrument-prof', label: 'Instrument Profitability', icon: 'fa fa-user', to: '/User/RiskMonitoring/InstrumentProfitability', permission: 'User.RiskMonitoring.InstrumentProfitability' },
          { key: 'market-abuse', label: 'Market Abuse', icon: 'fe fe-alert-triangle', to: '/User/RiskMonitoring/MarketAbuse', permission: 'User.RiskMonitoring.MarketAbuse' },
          { key: 'related-clients', label: 'Related Clients', icon: 'fas fa-user', to: '/User/RiskMonitoring/RelatedClients', permission: 'User.RiskMonitoring.RelatedClients' },
          { key: 'overnights', label: 'Overnights', icon: 'fe fe-file-text', to: '/User/RiskMonitoring/Overnights', permission: 'User.RiskMonitoring.Overnights' },
          { key: 'trade-blotter', label: 'Trade Blotter', icon: 'fe fe-book-open', to: '/User/RiskMonitoring/TradeBlotter', permission: 'User.RiskMonitoring.TradeBlotter', live: true },
        ],
      },
      {
        key: 'revenue-share', label: 'Revenue Share', icon: 'fa fa-dollar-sign', permission: 'User.RevenueShare',
        children: [
          { key: 'rev-results', label: 'Rev Share Results', icon: 'fa fa-users', to: '/User/RevenueShare/RevShareResults', permission: 'User.RevenueShare.RevShareResults', migrated: true },
          { key: 'rev-deals', label: 'Rev Share Deals', icon: 'fa fa-gears', to: '/User/RevenueShare/RevShareDeals', permission: 'User.RevenueShare.RevShareDeals', migrated: true },
        ],
      },
      { key: 'swap-free', label: 'Swap Free Control', icon: 'fe fe-settings', to: '/User/SwapFreeControl', permission: 'User.SwapFreeControl', migrated: true },
      { key: 'margin-control', label: 'Margin Control', icon: 'fe fe-settings', to: '/User/MarginControl', permission: 'User.MarginControl', migrated: true },
      {
        key: 'trader-surveillance', label: 'Trader Surveillance', icon: 'fe fe-shield', permission: 'User.TraderSurveillance',
        children: [
          { key: 'suspicious-trades', label: 'Suspicious Trades', icon: 'fe fe-alert-octagon', to: '/User/TraderSurveillance/SuspiciousTrades', permission: 'User.TraderSurveillance.SuspiciousTrades', migrated: true, live: true },
          { key: 'alerts-profile', label: 'Alerts Profile', icon: 'fe fe-user-check', to: '/User/TraderSurveillance/AlertsProfile', permission: 'User.TraderSurveillance.AlertsProfile', migrated: true },
        ],
      },
      { key: 'onboarding', label: 'Onboarding', icon: 'fa fa-list-check', to: '/User/Onboarding', permission: 'User.Onboarding', migrated: true },
      { key: 'terminal-mgmt', label: 'LP Management', icon: 'fa fa-cog', to: '/User/TerminalManagement', permission: 'User.TerminalManagement', migrated: true },
      {
        key: 'regulatory', label: 'Regulatory Monitoring', icon: 'fe fe-file-text', permission: 'User.RegulatoryMonitoring',
        children: [
          { key: 'custom-reports', label: 'Custom Reports', icon: 'fe fe-file-text', to: '/User/RegulatoryMonitoring/CustomReports', permission: 'User.RegulatoryMonitoring', migrated: true },
        ],
      },
      {
        key: 'safeguarding', label: 'Safeguarding & Reconciliation', icon: 'fe fe-shield', permission: 'User.SafeguardingReconciliation',
        children: [
          { key: 'providers-assets', label: 'Providers Assets', icon: 'fa fa-bank', to: '/User/DailyReconciliation/ProvidersAssets', permission: 'User.SafeguardingReconciliation.ProvidersAssets', migrated: true },
          { key: 'corporate-funds', label: 'Corporate Funds', icon: 'fa fa-building', to: '/User/DailyReconciliation/CorporateFunds', permission: 'User.SafeguardingReconciliation.CorporateFunds', migrated: true },
          { key: 'clients-assets', label: 'Client Assets', icon: 'fa fa-briefcase', to: '/User/DailyReconciliation/ClientsAssets', permission: 'User.SafeguardingReconciliation.ClientsAssets', migrated: true },
          { key: 'reconciliation', label: 'Reconciliation', icon: 'fa fa-balance-scale', to: '/User/DailyReconciliation/Reconciliation', permission: 'User.SafeguardingReconciliation.Reconciliation', migrated: true },
          {
            key: 'matching-engine', label: 'Matching Engine', icon: 'fa fa-link', permission: 'User.SafeguardingReconciliation.MatchingEngine',
            children: [
              { key: 'me-ecommpay-usd', label: 'EcommPay USD', to: '/User/DailyReconciliation/ProviderStatements/EcommPayUsd', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-ecommpay-eur', label: 'EcommPay EUR', to: '/User/DailyReconciliation/ProviderStatements/EcommPayEur', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-ecommpay-gbp', label: 'EcommPay GBP', to: '/User/DailyReconciliation/ProviderStatements/EcommPayGbp', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-cml-chf', label: 'CML CHF', to: '/User/DailyReconciliation/ProviderStatements/CmlChf', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-cml-gbp', label: 'CML GBP', to: '/User/DailyReconciliation/ProviderStatements/CmlGbp', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-cml-eur', label: 'CML EUR', to: '/User/DailyReconciliation/ProviderStatements/CmlEur', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-cml-usd', label: 'CML USD', to: '/User/DailyReconciliation/ProviderStatements/CmlUsd', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-cml-sek', label: 'CML SEK', to: '/User/DailyReconciliation/ProviderStatements/CmlSek', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-cml-dkk', label: 'CML DKK', to: '/User/DailyReconciliation/ProviderStatements/CmlDkk', permission: 'User.SafeguardingReconciliation.MatchingEngine.ApmProviders', migrated: true },
              { key: 'me-logs', label: 'Logs', to: '/User/DailyReconciliation/MatchingEngineLogs', permission: 'User.SafeguardingReconciliation.MatchingEngine.Logs', migrated: true },
            ],
          },
          {
            key: 'me-requests', label: 'Matching Engine Requests', icon: 'fe fe-check-square', permission: 'User.SafeguardingReconciliation.MatchingEngineRequests',
            children: [
              { key: 'me-req-list', label: 'Requests', icon: 'fa fa-inbox', to: '/User/DailyReconciliation/MatchingEngineRequests/Requests', permission: 'User.SafeguardingReconciliation.MatchingEngineRequests.Requests', migrated: true },
              { key: 'me-req-history', label: 'History', icon: 'fa fa-history', to: '/User/DailyReconciliation/MatchingEngineRequests/History', permission: 'User.SafeguardingReconciliation.MatchingEngineRequests.History', migrated: true },
            ],
          },
          { key: 'money-transactions', label: 'Money Transactions', icon: 'fa fa-exchange', to: '/User/DailyReconciliation/MoneyTransactions', permission: 'User.SafeguardingReconciliation.MoneyTransactions', migrated: true },
          {
            key: 'providers-requests', label: 'Providers Requests', icon: 'fe fe-check-square', permission: 'User.SafeguardingReconciliation.ProvidersRequests',
            children: [
              { key: 'pr-list', label: 'Requests', icon: 'fa fa-inbox', to: '/User/DailyReconciliation/ProvidersRequests/Requests', permission: 'User.SafeguardingReconciliation.ProvidersRequests.Requests', migrated: true },
              { key: 'pr-history', label: 'History', icon: 'fa fa-history', to: '/User/DailyReconciliation/ProvidersRequests/History', permission: 'User.SafeguardingReconciliation.ProvidersRequests.History', migrated: true },
            ],
          },
          { key: 'edit-logs', label: 'Edit Logs', icon: 'fa fa-history', to: '/User/DailyReconciliation/EditLogs', permission: 'User.SafeguardingReconciliation.EditLogs', migrated: true },
        ],
      },
      { key: 'online-manager', label: 'Online Manager', icon: 'fas fa-user-cog', to: '/User/OnlineManager', permission: 'User.OnlineManager', migrated: true },
      { key: 'critical-mod', label: 'Critical Modification', icon: 'fas fa-history', to: '/User/CriticalModification', permission: 'User.CriticalModification', migrated: true },
      { key: 'faq', label: 'FAQ', icon: 'fa fa-question-circle', to: '/User/FAQ' },
    ],
  },
];

// Host users (no tenant) — matches HostMenuContributor + promoted Administration.
const HOST_SECTIONS: MenuSection[] = [
  {
    // Legacy grouping: a single Admin group (Clients, Users, Roles, Deployment Notice),
    // with Settings kept here too; Trader Surveillance comes right after Admin.
    label: 'Admin',
    items: [
      { key: 'clients', label: 'Clients', icon: 'fa fa-building', to: '/host/tenants', permission: 'AbpTenantManagement.Tenants', migrated: true },
      { key: 'users', label: 'Users', icon: 'fa fa-users', to: '/admin/identity/users', permission: 'AbpIdentity.Users', migrated: true },
      { key: 'roles', label: 'Roles', icon: 'fa fa-user-shield', to: '/admin/identity/roles', permission: 'AbpIdentity.Roles', migrated: true },
      { key: 'deployment-notice', label: 'Deployment Notice', icon: 'fa fa-bullhorn', to: '/host/deployment-notice', permission: 'HostPages.DeploymentNotice', migrated: true },
      { key: 'settings', label: 'Settings', icon: 'fa fa-sliders-h', to: '/admin/setting-management', permission: 'SettingManagement.TimeZone', migrated: true },
    ],
  },
  {
    label: 'Trader Surveillance',
    items: [
      { key: 'ts-templates', label: 'TS Alert Profile Templates', icon: 'fa fa-clipboard-list', to: '/host/trader-surveillance/alert-profile-templates', permission: 'HostPages.TraderSurveillanceTemplates', migrated: true },
    ],
  },
  {
    label: 'Caching',
    items: [
      { key: 'market-data', label: 'Market Data', icon: 'fa fa-line-chart', to: '/host/market-data-integration', permission: 'HostPages.MarketDataIntegration', migrated: true },
    ],
  },
  {
    label: 'Symbols',
    items: [
      { key: 'cents', label: 'Cents', icon: 'fa fa-money-bill', to: '/host/cents', permission: 'HostPages.Cents', migrated: true },
      { key: 'global-symbols', label: 'Global Symbols', icon: 'fa fa-globe', to: '/host/global-symbols', permission: 'HostPages.GlobalSymbols', migrated: true },
    ],
  },
  {
    label: 'Tracking',
    items: [
      { key: 'online-users', label: 'Online Users', icon: 'fa fa-users', to: '/host/online-users', permission: 'HostPages.OnlineUsers', migrated: true },
      { key: 'login-activity', label: 'Login Activity', icon: 'fa fa-user-clock', to: '/host/login-activity', permission: 'HostPages.LoginActivity', migrated: true },
      { key: 'terminal-requests', label: 'LP Requests Management', icon: 'fa fa-clipboard-list', to: '/host/terminal-requests', permission: 'HostPages.TerminalRequestsManagement', migrated: true },
    ],
  },
  {
    label: 'FAQ',
    items: [
      { key: 'faq-host', label: 'FAQ Admin', icon: 'fa fa-question-circle', to: '/host/faq', permission: 'HostPages.CreateFaq', migrated: true },
    ],
  },
];

// Flattened (route path → required permission) map across EVERY side (host +
// admin + user), unfiltered. Used by the route-permission guard so a page reached
// by typing the URL is validated client-side with the same permission the sidebar
// uses — the backend [Authorize] remains the real boundary.
function collectPerms(items: MenuItem[], out: { to: string; permission?: string }[]) {
  for (const i of items) {
    if (i.to && i.to !== '#') out.push({ to: i.to.toLowerCase(), permission: i.permission });
    if (i.children) collectPerms(i.children, out);
  }
}

const ALL_ROUTE_PERMS: { to: string; permission?: string }[] = (() => {
  const out: { to: string; permission?: string }[] = [];
  [...ADMIN_SECTIONS, ...USER_SECTIONS, ...HOST_SECTIONS].forEach((s) => collectPerms(s.items, out));
  // Longest paths first so nested routes match before their parents.
  return out.sort((a, b) => b.to.length - a.to.length);
})();

// The permission required for a given pathname (undefined = no gate / not found).
export function requiredPermissionForPath(pathname: string): string | undefined {
  const p = pathname.toLowerCase();
  const hit = ALL_ROUTE_PERMS.find((e) => p === e.to || p.startsWith(`${e.to}/`));
  return hit?.permission;
}

function filterItems(items: MenuItem[], can: (p?: string | null) => boolean): MenuItem[] {
  return items
    .filter((i) => can(i.permission))
    .map((i) => (i.children ? { ...i, children: filterItems(i.children, can) } : i))
    .filter((i) => !i.children || i.children.length > 0 || i.to);
}

// Returns the permission-filtered menu sections for the current user.
// EITHER/OR by role, mirroring the legacy ABP menu contributors:
//   - Host (no tenant)          → the host menu (HostMenuContributor).
//   - Tenant admin (role=admin) → Administration + Settings ONLY
//     (ClientAdminMenuContributor): Users, Roles, Configuration, Email Recipients,
//     Providers — NOT the user feature tree.
//   - Non-admin tenant user     → the feature tree ONLY (UserMenuContributor).
// Permissions still filter within the chosen set, but the admin never gets the user
// feature pages and vice-versa.
export function buildMenu(
  isHost: boolean,
  isAdmin: boolean,
  can: (p?: string | null) => boolean,
): MenuSection[] {
  const sections = isHost ? HOST_SECTIONS : isAdmin ? ADMIN_SECTIONS : USER_SECTIONS;
  return sections
    .map((s) => ({ ...s, items: filterItems(s.items, can) }))
    .filter((s) => s.items.length > 0);
}
